import React from 'react';
import { Box, Typography } from '@mui/material';
import type { GridProps } from './types';
import { getBoxBorderStyle, getContrastColor, GRID_CONSTANTS } from './gridUtils';
import {
  isSquareClickable,
  getSquareLabel,
  getSquareTooltip,
  HitBadge,
  BadNumberBadge,
  CellContent,
  GridCell,
  MaybeTooltip,
  NumberHeaderCell,
} from './gridHelpers';

const { cellSize, headerSize } = GRID_CONSTANTS;

/**
 * StandardGrid renders the 10x10 grid for standard cell mode.
 * Handles individual square rendering, box neighbor borders, and standard number headers.
 */
export const StandardGrid: React.FC<GridProps> = ({
  board,
  grid,
  themeData,
  topNumbers,
  leftNumbers,
  topColor,
  leftColor,
  topAccent,
  leftAccent,
  numberSetKeys,
  hasMultipleNumberSets,
  getTopNumbersForKey,
  getLeftNumbersForKey,
  isActiveNumberSetKey,
  getHitCount,
  isBadNumberSquare,
  currentUserId,
  isManager,
  approvedSquares,
  userClaimedCount,
  highlightedCells,
  assignModeEnabled,
  assignModePlayerId,
  assignModeAtLimit,
  hideNames,
  onSquareClick,
  onClearSquare,
  hoveredCell,
  onCellMouseEnter,
  onCellMouseLeave,
  leftTeam,
  leftTeamType,
  gridHeight,
  leftTeamLogoUrl,
}) => {
  const getSquareColor = (square: typeof grid[0][0] | undefined, row: number, col: number) => {
    if (!square) return themeData.squareUnclaimed;
    const hitCount = getHitCount(row, col);
    if (hitCount > 0) return themeData.squareWinner;
    if (assignModeEnabled && assignModePlayerId) {
      if (square.ownerId === assignModePlayerId || square.offlinePlayerId === assignModePlayerId) {
        return themeData.squareSelected;
      }
    }
    if (square.ownerId) {
      return square.ownerId === currentUserId ? themeData.squareClaimedSelf : themeData.squareClaimedOther;
    }
    if (square.offlinePlayerId) return themeData.squareOfflinePlayer;
    return themeData.squareUnclaimed;
  };

  return (
    <>
      {/* Top Numbers */}
      {!hasMultipleNumberSets ? (
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          <Box sx={{ width: headerSize }} /> {/* Left team label spacer */}
          <Box sx={{ width: headerSize }} /> {/* Left numbers spacer */}
          {topNumbers.map((num, col) => (
            <Box key={col} sx={{ ml: 0.5 }}>
              <NumberHeaderCell
                num={num}
                isRevealed={board.numbersRevealed}
                isHighlighted={hoveredCell?.col === col}
                color={topColor}
                accentColor={topAccent}
                themeData={themeData}
                width={cellSize}
                height={headerSize}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ mb: 0.5 }}>
          {[...numberSetKeys].reverse().map((key) => {
            const numbersForKey = getTopNumbersForKey(key);
            const isActiveRow = isActiveNumberSetKey(key);
            return (
              <Box key={key} sx={{ display: 'flex', mb: 0.25 }}>
                <Box sx={{ width: headerSize }} />
                <Box sx={{ width: numberSetKeys.length * (headerSize + 2) }} />
                {numbersForKey.map((num, col) => (
                  <Box key={col} sx={{ ml: 0.5 }}>
                    <NumberHeaderCell
                      num={num}
                      isRevealed={board.numbersRevealed}
                      isHighlighted={hoveredCell?.col === col && isActiveRow}
                      color={topColor}
                      accentColor={topAccent}
                      themeData={themeData}
                      width={cellSize}
                      height={headerSize}
                      isActive={isActiveRow}
                    />
                  </Box>
                ))}
                <Box
                  sx={{
                    width: headerSize * 0.6,
                    height: headerSize,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    color: isActiveRow ? topColor : '#999',
                    opacity: isActiveRow ? 1 : 0.6,
                    ml: 0.5,
                  }}
                >
                  {key}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Main Grid Row: Left Team + Left Numbers + Grid Cells */}
      <Box sx={{ display: 'flex' }}>
        {/* Left Team Label */}
        <Box
          sx={{
            width: headerSize,
            height: gridHeight,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontWeight: 'bold',
              color: leftColor,
              whiteSpace: 'nowrap',
              fontSize: '1.1rem',
            }}
          >
            {leftTeam} ({leftTeamType})
          </Typography>
          {leftTeamLogoUrl && (
            <Box
              component="img"
              src={leftTeamLogoUrl}
              alt={leftTeam}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
              sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
            />
          )}
        </Box>

        {/* Left Numbers */}
        {!hasMultipleNumberSets ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {leftNumbers.map((num, row) => (
              <Box key={row} sx={{ mb: 0.5 }}>
                <NumberHeaderCell
                  num={num}
                  isRevealed={board.numbersRevealed}
                  isHighlighted={hoveredCell?.row === row}
                  color={leftColor}
                  accentColor={leftAccent}
                  themeData={themeData}
                  width={headerSize}
                  height={cellSize}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            {[...numberSetKeys].reverse().map((key) => {
              const numbersForKey = getLeftNumbersForKey(key);
              const isActiveCol = isActiveNumberSetKey(key);
              return (
                <Box key={key} sx={{ display: 'flex', flexDirection: 'column', mr: 0.25 }}>
                  <Box
                    sx={{
                      width: headerSize,
                      height: headerSize * 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      color: isActiveCol ? leftColor : '#999',
                      opacity: isActiveCol ? 1 : 0.6,
                      mb: 0.25,
                    }}
                  >
                    {key}
                  </Box>
                  {numbersForKey.map((num, row) => (
                    <Box key={row} sx={{ mb: 0.5 }}>
                      <NumberHeaderCell
                        num={num}
                        isRevealed={board.numbersRevealed}
                        isHighlighted={hoveredCell?.row === row && isActiveCol}
                        color={leftColor}
                        accentColor={leftAccent}
                        themeData={themeData}
                        width={headerSize}
                        height={cellSize}
                        isActive={isActiveCol}
                      />
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Grid Cells */}
        <Box onMouseLeave={onCellMouseLeave} data-testid="squares-grid">
          {hasMultipleNumberSets && (
            <Box sx={{ height: headerSize * 0.5 + 2, mb: 0.25 }} />
          )}
          {grid.map((row, rowIndex) => (
            <Box key={rowIndex} sx={{ display: 'flex', mb: 0.5 }}>
              {row.map((square, colIndex) => {
                const clickable = isSquareClickable(
                  square, board.status, isManager, currentUserId,
                  assignModeEnabled, assignModePlayerId, assignModeAtLimit,
                  approvedSquares, userClaimedCount, onSquareClick, onClearSquare,
                );
                const hitCount = getHitCount(rowIndex, colIndex);
                const bgColor = getSquareColor(square, rowIndex, colIndex);
                const textColor = getContrastColor(bgColor);
                const tooltipText = getSquareTooltip(square, hitCount, hideNames);
                const isHighlightedFromGameLog = highlightedCells?.some(
                  cell => cell.row === rowIndex && cell.col === colIndex
                ) ?? false;
                const hasActiveHighlight = highlightedCells && highlightedCells.length > 0;
                const isDimmed = hasActiveHighlight && !isHighlightedFromGameLog;
                const isBadSquare = isBadNumberSquare(rowIndex, colIndex);

                const boxNeighborConfig = board.payoutConfig?.boxNeighborPayouts;
                const boxBorderStyle = boxNeighborConfig?.enabled
                  ? getBoxBorderStyle(
                      rowIndex, colIndex, boxNeighborConfig.boxSize,
                      themeData.boxNeighborBorderColor || '#1976d2',
                      themeData.boxNeighborBorderWidth || 3,
                    )
                  : {};

                const squareNumber = rowIndex * 10 + colIndex + 1;
                const { icon, handle } = getSquareLabel(square, squareNumber.toString(), hideNames);

                const cellContent = (
                  <GridCell
                    width={cellSize}
                    height={cellSize}
                    bgColor={bgColor}
                    isClickable={clickable}
                    isHighlighted={isHighlightedFromGameLog}
                    isDimmed={!!isDimmed}
                    highlightScale="1.15"
                    hoverScale="1.05"
                    extraSx={boxBorderStyle}
                    onClick={() => onSquareClick(rowIndex, colIndex)}
                    onMouseEnter={() => onCellMouseEnter(rowIndex, colIndex)}
                  >
                    <HitBadge count={hitCount} themeData={themeData} />
                    <BadNumberBadge isBad={isBadSquare} />
                    <CellContent
                      icon={icon}
                      handle={handle}
                      textColor={textColor}
                      fontSize={{ withIcon: '0.5rem', withoutIcon: '0.65rem' }}
                    />
                  </GridCell>
                );

                return (
                  <MaybeTooltip key={colIndex} tooltipText={tooltipText} reactKey={colIndex}>
                    {cellContent}
                  </MaybeTooltip>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};
